import { useRef, useState, useTransition, type ReactNode } from "react";
import { toast } from "sonner";
import { Icone } from "@/components/Icone";
import { responderAction } from "@/web/src/acoes";

/**
 * Onde a equipe escreve — uma pílula com o botão redondo de enviar ao lado.
 *
 * Enter envia, Shift+Enter quebra linha — como no WhatsApp Web, porque é de
 * lá que vem o hábito de quem vai usar isto o dia todo. A caixa cresce com o
 * texto até um teto, para uma resposta longa não empurrar a conversa toda.
 * `antes` é o que fica à esquerda dentro da pílula (o botão de modelo).
 */
export function Compositor({ telefone, antes }: { telefone: string; antes?: ReactNode }) {
  const [texto, setTexto] = useState("");
  const [enviando, iniciar] = useTransition();
  const ref = useRef<HTMLTextAreaElement>(null);

  function enviar() {
    const t = texto.trim();
    if (!t || enviando) return;
    iniciar(async () => {
      const r = await responderAction(telefone, t);
      if (!r.ok) {
        toast.error(r.erro);
        return;
      }
      setTexto("");
      if (ref.current) ref.current.style.height = "auto";
      ref.current?.focus();
    });
  }

  return (
    <div>
      <div className="flex items-end gap-2">
        <div className="flex min-w-0 flex-1 items-end gap-1 rounded-[24px] bg-[var(--color-cartao)] py-1 pr-2 pl-1 shadow-cartao focus-within:shadow-[0_0_0_1.5px_var(--color-marca)]">
          {antes}
          <textarea
            ref={ref}
            value={texto}
            onChange={(e) => {
              setTexto(e.target.value);
              e.target.style.height = "auto";
              e.target.style.height = `${Math.min(e.target.scrollHeight, 140)}px`;
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                enviar();
              }
            }}
            rows={1}
            disabled={enviando}
            placeholder="Escreva sua resposta"
            aria-label="Escreva sua resposta"
            className={
              "max-h-[140px] min-h-10 min-w-0 flex-1 resize-none bg-transparent py-2.5 text-[14.5px] leading-[1.45] text-[var(--color-tinta)] outline-none placeholder:text-[var(--color-fraco)] " +
              (antes ? "pl-1" : "pl-3")
            }
          />
        </div>
        <button
          type="button"
          onClick={enviar}
          disabled={enviando || !texto.trim()}
          aria-label={enviando ? "Enviando" : "Enviar"}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[var(--color-marca)] text-[var(--color-sobre-marca)] transition-[filter,transform] hover:brightness-110 active:scale-95 disabled:opacity-40"
        >
          <Icone nome="enviar" className="h-[18px] w-[18px]" />
        </button>
      </div>
      <p className="mt-1.5 hidden text-center text-[11.5px] text-[var(--color-fraco)] md:block">Enter envia · Shift + Enter quebra a linha</p>
    </div>
  );
}
